import {useMemo, useState} from "react";
import {Calendar as CalendarIcon} from "lucide-react";
import type {DateRange} from "react-day-picker";
import {
  ADMIN_DATE_RANGE_PRESETS,
  adminDateRangeLabel,
  resolveAdminDateRange,
  type AdminDateRangePreset,
  type AdminDateRangeValue
} from "@/lib/adminDateRange";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";

type Props = {
  value: AdminDateRangeValue;
  onChange: (value: AdminDateRangeValue) => void;
  className?: string;
  disabled?: boolean;
};

export function AdminDateRangePicker({value, onChange, className, disabled}: Props) {
  const [open, setOpen] = useState(false);
  const selectedRange = useMemo<DateRange | undefined>(() => {
    const {from, to} = resolveAdminDateRange(value);
    if(!from && !to) return undefined;
    return {from, to};
  }, [value]);

  function applyPreset(preset: AdminDateRangePreset) {
    if(preset === "custom") {
      onChange({preset: "custom", from: value.from, to: value.to});
      return;
    }
    onChange({preset});
    setOpen(false);
  }

  function applyCustomRange(range: DateRange | undefined) {
    if(!range?.from) {
      onChange({preset: "all"});
      return;
    }
    onChange({
      preset: "custom",
      from: range.from,
      to: range.to ?? range.from
    });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn("h-9 min-w-[220px] justify-start text-left font-normal", className)}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{adminDateRangeLabel(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="admin-overlay-surface w-auto p-0">
        <div className="flex flex-col md:flex-row">
          <div className="flex flex-col gap-0.5 border-b p-2 md:w-52 md:border-b-0 md:border-r">
            {ADMIN_DATE_RANGE_PRESETS.filter((p) => p.id !== "custom").map((preset) => (
              <Button
                key={preset.id}
                type="button"
                variant={value.preset === preset.id ? "secondary" : "ghost"}
                size="sm"
                className="h-8 justify-start px-2 text-sm font-normal"
                onClick={() => applyPreset(preset.id)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="p-2">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={selectedRange}
              onSelect={applyCustomRange}
              defaultMonth={selectedRange?.from}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
